import React, { useState, useRef } from 'react';
import { Form, Formik, Field } from 'formik';
import * as Yup from 'yup';
import { FormikInputField } from './fields/formikInputField';
import { FormikTextareaField } from './fields/formikTextareaField';
import { ImageUpload } from './fields/imageUpload';
import { connect } from 'react-redux';
import { setSelectedCategory } from '../reducers/entryState';
import { setCompetitionTab } from '../../Navigation/currentTab';
import { TAB_CATEGORIES, TAB_ENTRY_SUBMITTED } from '../../enums';
import { getLabel } from '../../Language/labels';
import { submitEntry } from '../api/submitEntry';
import { Processing } from './processing';
import { TransitionWrapper } from '../../Helpers/transitionWrapper';
import { get } from 'lodash';
import { RequiredFieldMarker } from './fields/requiredFieldMarker';
import { getErrorMessage } from '../../Helpers/validation/apiErrorMessageHandler';
import { Search } from './fields/search';
import { ValidationMessage, ForceValidationMessage } from '../../Helpers/validation/validationMessage';
import { competitionInstitutionRequired, genreInstitutionRequired } from '../selectors/competitionSelector';

// use institution_required from here aka the Category (below), and institution_required on genre (there should only be 1) and competion
// should end up with category_institution_required || genre_institution_required || institution_required if this resolves to 1 the institution_required === true
// make a selector for category_institution_required and genre_institution_required

const EntryUpload = ({ dispatch,
  entryState: { name, description, id, genre_id, image: { cdn }, institution_required },
  loginState,
  institutions,
  genre_InstitutionRequired,
  competition_InstitutionRequired }) => {

  const [processing, setProcessing] = useState(false);
  const [apiError, setApiError] = useState('');
  const formRef = useRef();
  const maxCharCount = 1000;

  const entrySchema = Yup.object().shape({
    entry_name: Yup.string().required(getLabel('entryNameRequired')),
    video: Yup.string().url(getLabel('entryValidUrlRequired')).required(getLabel('entryVideoRequired')),
    description: Yup.string().max(maxCharCount, getLabel('maxLength150')).required(getLabel('entryDescriptionRequired')),
    thumbnail: Yup.mixed().required(getLabel('entryThumbnailRequired')),
    video_make: Yup.string().required(getLabel('entryMakeRequired')),
    // video_make_other: Yup.string().when('video_make', {
    //   is: 'Other',
    //   then: Yup.string().required(getLabel('entryMakeRequired')),
    //   otherwise: Yup.string()
    // }),
    video_model: Yup.string().required(getLabel('entryModelRequired')),
    ai_assisted: Yup.string().required(getLabel('entryUsingAiRequired')),
    ai_assisted_how: Yup.string().when('ai_assisted', {
      is: 'true',
      then: Yup.string().required(getLabel('entryAiDetailsRequired')),
      otherwise: Yup.string()
    }),
    bio: Yup.string().max(maxCharCount, getLabel('maxLength150')).required(getLabel('requiredField'))
  })

  const entrySchemaWithInstitution = Yup.object().shape({
    entry_name: Yup.string().required(getLabel('entryNameRequired')),
    video: Yup.string().url(getLabel('entryValidUrlRequired')).required(getLabel('entryVideoRequired')),
    description: Yup.string().max(maxCharCount, getLabel('maxLength150')).required(getLabel('entryDescriptionRequired')),
    thumbnail: Yup.mixed().required(getLabel('entryThumbnailRequired')),
    institution: Yup.number().required(getLabel('entryInstitution')),
    video_make: Yup.string().required(getLabel('entryMakeRequired')),
    // video_make_other: Yup.string().when('video_make', {
    //   is: 'Other',
    //   then: Yup.string().required(getLabel('entryMakeRequired')),
    //   otherwise: Yup.string()
    // }),
    video_model: Yup.string().required(getLabel('entryModelRequired')),
    ai_assisted: Yup.string().required(getLabel('entryUsingAiRequired')),
    ai_assisted_how: Yup.string().when('ai_assisted', {
      is: 'true',
      then: Yup.string().required(getLabel('entryAiDetailsRequired')),
      otherwise: Yup.string()
    }),
    course_name: Yup.string().required(getLabel('requiredField')),
    tutors_name: Yup.string().required(getLabel('requiredField')),
    tutors_email: Yup.string().email(getLabel('emailNotValid')).required(getLabel('emailRequired')),
    bio: Yup.string().max(maxCharCount, getLabel('maxLength150')).required(getLabel('requiredField')),
    institution_name: Yup.string().when('institution', {
      is: 0,
      then: Yup.string().required(getLabel('entryInstitution')),
      otherwise: Yup.string()
    }),
    school_country: Yup.string().when('institution', {
      is: 0,
      then: Yup.string().required(getLabel('entryInstitution')),
      otherwise: Yup.string()
    })
  })

  const selectCategory = () => {
    window.formValues = formRef.current.values;
    dispatch(setSelectedCategory(null));
    dispatch(setCompetitionTab(TAB_CATEGORIES));
  }

  const getVideoMakeInitialValue = () => {
    // video_make won't always be one of the options. If someone used the free text field (video_make_other)
    // set video_make to Other so we show the free text field
    // The free text field (video_make_other) will get the value of video_make regardless
    const videoMakeSelected = get(window, 'formValues.video_make', '');
    if (["Arri",
      "Blackmagic",
      "Canon",
      "Panasonic",
      "Red",
      "Sony",
      "Nikon",
      "Olympus"].includes(videoMakeSelected)) {
      return videoMakeSelected;
    } else if (videoMakeSelected.length > 0) {
      return "Other"
    } else {
      return '';
    }
  }

  const isInstitutionRequired = () => {
    return competition_InstitutionRequired || genre_InstitutionRequired || institution_required;
  }

  const renderInstitutionNameField = (formValues) => {
    if (isInstitutionRequired() && formValues.institution === 0) {
      return (
        <div className='form-control'>
          <span>{getLabel('institutionNotFoundLabel')}</span><RequiredFieldMarker />
          <Field name="institution_name" as={FormikInputField} type="text" />
        </div>
      )
    } else {
      return null;
    }
  }

  return (
    <TransitionWrapper>
      <div className='screen-title-wrapper'>
        <h1 className='screen-title'>{getLabel('entryUploadTitle')}</h1>
      </div>
      <div className='form entry-upload'>
        {processing && <Processing title={getLabel('entryProcessing')} />}
        <div className='entry-info'>
          {/* <img className='w-full' src={cdn} alt={name} /> */}
          <div className='entry-info-text'>
            <h1>{name}</h1>
            <p>{description}</p>
            <div>
              <button onClick={selectCategory} type="button" className='form-submit'>{getLabel('entryChangeCategory')}</button>
            </div>
          </div>
        </div>
        <div className='entry-upload-form'>
          {apiError.length > 0 && <div className='text-2xl'>{apiError}</div>}
          <Formik
            innerRef={formRef}
            initialValues={{
              entry_name: get(window, 'formValues.entry_name', ''),
              video: get(window, 'formValues.video', ''),
              description: get(window, 'formValues.description', ''),
              thumbnail: get(window, 'formValues.thumbnail', ''),
              video_make: getVideoMakeInitialValue(),
              video_model: get(window, 'formValues.video_model', ''),
              ai_assisted_how: get(window, 'formValues.ai_assisted_how', ''),
              ai_assisted: get(window, 'formValues.ai_assisted', ''),
              // video_make_other is an app field and does not exist on the API. We're using it because we still need a free text field for the camera brand.
              //video_make_other: get(window, 'formValues.video_make', ''),
              course_name: get(window, 'formValues.course_name', ''),
              tutors_name: get(window, 'formValues.tutors_name', ''),
              tutors_email: get(window, 'formValues.tutors_email', ''),
              video_password: get(window, 'formValues.video_password', ''),
              school_country: get(window, 'formValues.school_country', ''),
              bio: get(window, 'formValues.bio', ''),
              institution_name: get(window, 'formValues.institution_name', '')
            }}
            validationSchema={() => isInstitutionRequired() === 0 ? entrySchema : entrySchemaWithInstitution}
            onSubmit={async (values, actions) => {

              // check video_make_other, if it has a value, use that for video_make
              // if (get(values, 'video_make_other', '').length > 0) {
              //   values.video_make = get(values, 'video_make_other', '');
              // }

              setApiError('');
              setProcessing(true);
              const submitEntryResult = await submitEntry(values, loginState, id).catch((error) => {
                console.log(error);
                setProcessing(false);
                const api_errors = get(error, 'response.data.errors', '');
                setApiError(getErrorMessage(api_errors));
                //alert(`An error occured while processing the entry, ${JSON.stringify(api_errors)}`);
              });

              if (submitEntryResult) {

                if (submitEntryResult.status === 200) {
                  if (window.fbq) {
                    window.fbq('track', 'Lead');
                  }

                  if (window.dataLayer) {
                    window.dataLayer.push({ 'event': 'lead' });
                  }
                }

                window.setTimeout(() => {
                  setProcessing(false);
                  window.formValues = null;
                  dispatch(setCompetitionTab(TAB_ENTRY_SUBMITTED));
                }, 4000);
              }
            }}>
            {({ values, errors, touched, handleChange }) => (
              <Form>
                {/* <pre>{JSON.stringify(errors, null, '\t')}</pre> */}
                <label className='form-control' htmlFor="entry_name">
                  <span>{getLabel('entryName')}</span><RequiredFieldMarker />
                  <Field name="entry_name" as={FormikInputField} type="text" />
                </label>

                {/* NEW FIELDS */}
                {/* 'course_name', */}
                {/* 'tutors_name', */}
                {/* 'tutors_email', */}


                {isInstitutionRequired() > 0 &&
                  <div className='form-control'>
                    <span>{getLabel('institutionLabel')}</span><RequiredFieldMarker />
                    <Field name="institution" as={Search} selectOptions={institutions} />
                    <ForceValidationMessage error={errors.institution} />
                  </div>}

                {renderInstitutionNameField(values)}

                {isInstitutionRequired() > 0 && values.institution === 0 &&
                  <label className='form-control' htmlFor="school_country">
                    <span>{getLabel('schoolCountry')}</span><RequiredFieldMarker />
                    <select id="school_country" onChange={handleChange} value={values.school_country}>
                      <option value="">{getLabel('registerSelectCountry')}</option>
                      {window.country_options.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                    <ValidationMessage error={errors.school_country} touched={touched.school_country} />
                  </label>}


                {isInstitutionRequired() > 0 && <label className='form-control' htmlFor="course_name">
                  <span>{getLabel('courseName')}</span><RequiredFieldMarker />
                  <Field name="course_name" as={FormikInputField} type="text" />
                </label>}

                {isInstitutionRequired() > 0 && <label className='form-control' htmlFor="tutors_name">
                  <span>{getLabel('tutorsName')}</span><RequiredFieldMarker />
                  <Field name="tutors_name" as={FormikInputField} type="text" />
                </label>}

                {isInstitutionRequired() > 0 && <label className='form-control' htmlFor="tutors_email">
                  <span>{getLabel('tutorsEmail')}</span><RequiredFieldMarker />
                  <Field name="tutors_email" as={FormikInputField} type="text" />
                </label>}

                <label className='form-control' htmlFor="video">
                  <span>{getLabel('entryVideo')}</span><RequiredFieldMarker />
                  <Field name="video" as={FormikInputField} type="text" />
                  {/* <span>{getLabel('entryVideoHelpText')}</span> */}
                </label>
                <label className='form-control' htmlFor="video">
                  <span>{getLabel('filmPassword')}</span>
                  <Field name="video_password" as={FormikInputField} type="text" />
                  {/* <span>{getLabel('entryVideoHelpText')}</span> */}
                </label>
                <label className='form-control' htmlFor="description">
                  <span>{getLabel('entryDescription')}</span><RequiredFieldMarker />
                  <Field name="description" as={FormikTextareaField} type="text" />
                </label>

                <label className='form-control' htmlFor="video_make">
                  <span>{getLabel('entryMake')}</span><RequiredFieldMarker />
                  <select id="video_make" onChange={handleChange} value={values.video_make}>
                    <option value="">{getLabel('select')}</option>
                    <option value="ARRI">ARRI</option>
                    <option value="Blackmagic">Blackmagic</option>
                    <option value="Canon">Canon</option>
                    <option value="Sony">Sony</option>
                    <option value="Casio">Casio</option>
                    <option value="dji">dji</option>
                    <option value="Fujifilm">Fujifilm</option>
                    <option value="Hasselblad">Hasselblad</option>
                    <option value="Huawei">Huawei</option>
                    <option value="iPhone / Apple">iPhone / Apple</option>
                    <option value="Kodak">Kodak</option>
                    <option value="Leica">Leica</option>
                    <option value="Mamiya">Mamiya</option>
                    <option value="Olympus">Olympus</option>
                    <option value="Other">Other</option>
                    <option value="Other mobile phone">Other mobile phone</option>
                    <option value="Panasonic">Panasonic</option>
                    <option value="Pentax">Pentax</option>
                    <option value="Phase One">Phase One</option>
                    <option value="RED">RED</option>
                    <option value="Ricoh">Ricoh</option>
                    <option value="Samsung">Samsung</option>
                    <option value="Sigma">Sigma</option>
                  </select>
                  <ValidationMessage error={errors.video_make} touched={touched.video_make} />
                </label>

                {/* {values.video_make === 'Other' &&
                  <label className='form-control' htmlFor="video_make_other">
                    <span>{getLabel('entryOtherMake')}</span><RequiredFieldMarker />
                    <Field name="video_make_other" as={FormikInputField} type="text" />
                  </label>} */}

                <label className='form-control' htmlFor="video_model">
                  <span>{getLabel('entryModel')}</span><RequiredFieldMarker />
                  <Field name="video_model" as={FormikInputField} type="text" />
                </label>

                <label className='form-control' htmlFor="bio">
                  <span>{getLabel('bio')}</span><RequiredFieldMarker />
                  <Field name="bio" as={FormikTextareaField} type="text" />
                </label>

                <label className='form-control text-white is-required' style={{ paddingTop: 4 }} htmlFor="ai_assisted">
                  <span data-last="*">{getLabel('entryUsingAi')}</span>
                </label>

                <div className='flex mb-4'>
                  <label className='flex items-center mr-12'>
                    <Field type="radio" name="ai_assisted" value="true" />
                    <span className='ml-2'>{getLabel('yes')}</span>
                  </label>

                  <label className='flex items-center'>
                    <Field type="radio" name="ai_assisted" value="false" />
                    <span className='ml-2'>{getLabel('no')}</span>
                  </label>
                </div>
                {errors.ai_assisted && touched.ai_assisted && <span>{getLabel('entryUsingAiRequired')}</span>}

                {values.ai_assisted === 'true' &&
                  <label className='form-control' htmlFor="ai_assisted_how">
                    <span>{getLabel('entryUsingAiDetails')}</span><RequiredFieldMarker />
                    <Field name="ai_assisted_how" as={FormikTextareaField} type="text" />
                  </label>}

                <div className='form-control'>
                  <span>{getLabel('entryThumbnail')}</span><RequiredFieldMarker />
                  <Field name="thumbnail" as={ImageUpload} />

                </div>
                <div className='image-upload-help-text'>{getLabel('imageDropZone')}</div>
                <div className='form-submit-wrapper'>
                  <button className='form-submit' type="submit">{getLabel('entryButton')}</button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </TransitionWrapper>
  )
};

const mapStateToProps = (state) => {
  return {
    entryState: state.entryState,
    loginState: state.loginState,
    institutions: state.institutions,
    genre_InstitutionRequired: genreInstitutionRequired(state),
    competition_InstitutionRequired: competitionInstitutionRequired(state)
  }
}

export default connect(mapStateToProps)(EntryUpload);